import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { addDays, format, isValid, parse } from "date-fns";
import { Container } from "reactstrap";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { isMSP } from "../../helpers/util_helper";
import { useAccounts } from "../../components/Common/HelperComponents/GetDataFromStore";
import { getAccountCloudType } from "../../CloudProviderContext";
import { AWS_REGION_CODES } from "../Compliance/AuditHub/utils";
import { AWS_ACCOUNT_NUMBER } from "../Compliance/AuditHub/constants";
import { getCustomerOrgAccounts } from "../../store/customer/customerSlice";
import {
  createOnboardedCustomer,
  findPartnerCustomerProfile,
  getPartnerCustomer,
  listPartnerPrograms,
  updateOrganizationCustomer,
} from "../../helpers/partnerCentral_helper";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  accentOutlinedButtonSx,
  AuditHubThemeProvider,
  auditHubPageSx,
  outlinedButtonSx,
  primaryButtonSx,
  tableShellSx,
} from "../Compliance/AuditHub/theme";
import DueDateField from "../Compliance/AuditHub/DueDateField";
import {
  buildSelectProps,
  fieldSx,
  menuItemSx,
  selectFieldSx,
} from "./formFieldStyles";
import {
  formTableActionBtnSx,
  formTableActionsWrapSx,
  formTableBodyCellSx,
  formTableCardSx,
  formTableDeleteBtnSx,
  formTableHeadCellSx,
} from "./formTableStyles";
import PayerPayeeAccountsField from "./PayerPayeeAccountsField";
import WholesaleProgramsField from "./WholesaleProgramsField";
import {
  buildJavaCustomerUpdatePayload,
  buildJavaOnboardingPayload,
  formFromCustomerRecord,
  resolveCommitmentMode,
  selectedCustomerAccountIds,
  validateActiveCustomer,
} from "./customerPayload";

const commitmentModeToggleSx = {
  flexWrap: "wrap",
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    px: 1.5,
    py: 0.75,
    color: AH_TEXT,
    borderColor: AH_BORDER,
    "&.Mui-selected": {
      backgroundColor: AH_ACCENT,
      color: AH_WHITE,
      borderColor: AH_ACCENT,
      "&:hover": { backgroundColor: AH_ACCENT_HOVER },
    },
  },
};

const CUSTOMER_PATH = "/partner-central/customer";

const PAYMENT_TERMS = ["Net 30", "Net 45", "Net 60"];

const PRICING_MODELS = [
  "Standard List Minus Margin",
  "Cost-Plus Markup",
  "Custom Rate Card",
];

const SERVICE_EXCLUSIONS = [
  "Exclude AWS Marketplace",
  "Exclude Support Fees",
  "Exclude Taxes",
];

const labelSx = {
  display: "block",
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: 1.4,
};

const sectionSx = {
  fontSize: 14,
  fontWeight: 700,
  color: AH_TEXT,
  letterSpacing: "-0.01em",
};

const regionChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const regionsSummary = (regions) => {
  if (!regions.length) return "All regions";
  return regions.length === 1 ? regions[0] : `${regions.length} regions`;
};

const chipSummary = (items, emptyLabel) => {
  if (!items.length) return emptyLabel;
  return items.length === 1 ? items[0] : `${items.length} selected`;
};

const dayAfterIso = (isoDate) => {
  if (!isoDate) return null;
  const parsed = parse(isoDate, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? addDays(parsed, 1) : null;
};

const parseNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,%/]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const formatCommitment = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return value;
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "—";
  const parsed = parse(isoDate, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : isoDate;
};

const emptyYear = () => ({
  id: `year-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label: "",
  startDate: "",
  endDate: "",
  commitment: "",
  discount: "",
});

const emptyTier = () => ({
  id: `tier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: "",
  threshold: "",
  discount: "",
});

const emptyForm = () => ({
  organizationName: "",
  customerId: "",
  primaryContactName: "",
  primaryContactEmail: "",
  customerOrgcode: "",
  organizationId: null,
  profileId: null,
  initialAccountNumbers: [],
  payerAccountNumbers: [],
  payeeAccountNumbers: [],
  /** Explicit "Payer:" chips from the Payee dropdown (Selected Accounts). */
  payeeUiPayerNumbers: [],
  regions: [],
  wholesalePrograms: [],
  startDate: "",
  endDate: "",
  paymentTerms: "",
  multiYearCommitment: "",
  commitmentDiscount: "",
  pricingModel: "",
  serviceExclusions: [],
  commitmentMode: "annual",
  years: [],
  tiers: [],
});

const FieldLabel = ({ htmlFor, children, required }) => (
  <Typography component="label" htmlFor={htmlFor} sx={labelSx}>
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }} aria-hidden="true">
        *
      </Box>
    ) : null}
  </Typography>
);

const AddCustomer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const allowed = isMSP();
  const accounts = useAccounts();
  const { customerOrgAccountsSuccess } = useSelector((state) => state.customer);
  const search = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const customerId = search.get("id");
  const isEdit = Boolean(customerId);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [payersHydrated, setPayersHydrated] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsError, setProgramsError] = useState("");
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [yearDraft, setYearDraft] = useState(emptyYear);
  const [yearDraftErrors, setYearDraftErrors] = useState({});
  const [editingYearId, setEditingYearId] = useState(null);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [tierDraft, setTierDraft] = useState(emptyTier);
  const [tierDraftErrors, setTierDraftErrors] = useState({});
  const [editingTierId, setEditingTierId] = useState(null);

  useEffect(() => {
    document.title = isEdit
      ? "Edit Customer | Partner Central | CloudNova App"
      : "Add Customer | Partner Central | CloudNova App";
  }, [isEdit]);

  useEffect(() => {
    if (!allowed) history.replace("/dashboard");
  }, [allowed, history]);

  useEffect(() => {
    dispatch(getCustomerOrgAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (!allowed || !isEdit) {
      setLoadingRecord(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingRecord(true);
    setPayersHydrated(false);
    setFormError("");
    getPartnerCustomer(customerId)
      .then((record) => {
        if (cancelled) return;
        setForm(formFromCustomerRecord(record));
      })
      .catch((error) => {
        if (cancelled) return;
        setFormError(error?.message || "Unable to load customer.");
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, isEdit, customerId]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setProgramsLoading(true);
    setProgramsError("");
    listPartnerPrograms({ statuses: ["DRAFT", "ACTIVE"] })
      .then((items) => {
        if (cancelled) return;
        // Create: unassigned only. Edit: also keep programs already on this customer.
        setAvailablePrograms(
          items.filter((program) => {
            if (program.customer_id == null) return true;
            return isEdit && String(program.customer_id) === String(customerId);
          })
        );
      })
      .catch((error) => {
        if (cancelled) return;
        setAvailablePrograms([]);
        setProgramsError(error?.message || "Unable to load wholesale programs.");
      })
      .finally(() => {
        if (!cancelled) setProgramsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, isEdit, customerId]);

  // Restore payer chips for edit from org-account payer IDs of saved payees.
  useEffect(() => {
    if (!isEdit || payersHydrated || loadingRecord) return;
    if (!form.payeeAccountNumbers?.length) {
      setPayersHydrated(true);
      return;
    }
    if (form.payerAccountNumbers?.length) {
      setPayersHydrated(true);
      return;
    }
    const payload = customerOrgAccountsSuccess;
    if (!payload) return;
    let data = payload?.data;
    if (
      data?.data &&
      typeof data.data === "object" &&
      !Array.isArray(data.data) &&
      data.account_number == null &&
      data.accountNumber == null
    ) {
      data = data.data;
    }
    if (!data || typeof data !== "object") return;
    const payeeSet = new Set(form.payeeAccountNumbers);
    const payers = new Set();
    const entries = Array.isArray(data) ? data : Object.values(data);
    entries.forEach((acc) => {
      if (!acc || typeof acc !== "object") return;
      const number = String(acc.account_number ?? acc.accountNumber ?? "").trim();
      if (!payeeSet.has(number)) return;
      const payer = String(acc.payer_account_id ?? acc.payerAccountId ?? "").trim();
      if (/^\d{12}$/.test(payer)) payers.add(payer);
      else if (/^\d{12}$/.test(number)) payers.add(number);
    });
    if (!payers.size) {
      // Fall back to treating authorized accounts as selectable payers.
      form.payeeAccountNumbers.forEach((id) => payers.add(id));
    }
    // GET accountNumbers that are themselves payers must pre-check in the Payee
    // dropdown / Selected Accounts (payeeUiPayerNumbers), not only top scope.
    const uiPayers = [...payers].filter((id) => payeeSet.has(id));
    setForm((prev) => ({
      ...prev,
      payerAccountNumbers: [...payers],
      payeeUiPayerNumbers: uiPayers,
    }));
    setPayersHydrated(true);
  }, [
    isEdit,
    payersHydrated,
    loadingRecord,
    form.payeeAccountNumbers,
    form.payerAccountNumbers,
    customerOrgAccountsSuccess,
  ]);

  const payerAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts
      .filter((account) => {
        const number = String(account?.accountNumber ?? "").trim();
        if (!AWS_ACCOUNT_NUMBER.test(number)) return false;
        return getAccountCloudType(account) !== "AZURE";
      })
      .map((account) => ({
        accountNumber: String(account.accountNumber).trim(),
        accountName: account.accountName || "",
      }));
  }, [accounts]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const leave = () => history.push(CUSTOMER_PATH);

  const toggleChip = (key, value) => {
    setForm((prev) => {
      const selected = prev[key] || [];
      const next = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      return { ...prev, [key]: next };
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleRegion = (code) => {
    setForm((prev) => {
      const next = prev.regions.includes(code)
        ? prev.regions.filter((region) => region !== code)
        : [...prev.regions, code];
      return { ...prev, regions: next };
    });
  };

  const openYearDialog = () => {
    setEditingYearId(null);
    setYearDraft(emptyYear());
    setYearDraftErrors({});
    setYearDialogOpen(true);
  };

  const openEditYearDialog = (year) => {
    setEditingYearId(year.id);
    setYearDraft({ ...year });
    setYearDraftErrors({});
    setYearDialogOpen(true);
  };

  const closeYearDialog = () => {
    setYearDialogOpen(false);
    setEditingYearId(null);
    setYearDraft(emptyYear());
    setYearDraftErrors({});
  };

  const setYearDraftField = (key, value) => {
    setYearDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "startDate" && next.endDate && value && next.endDate <= value) {
        next.endDate = "";
      }
      return next;
    });
    setYearDraftErrors((prev) => {
      if (!prev[key] && !(key === "startDate" && prev.endDate)) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === "startDate") delete next.endDate;
      return next;
    });
  };

  const saveYearDraft = () => {
    const nextErrors = {};
    if (!yearDraft.label.trim()) nextErrors.label = "Enter a program year identifier.";
    if (yearDraft.startDate && yearDraft.endDate && yearDraft.endDate < yearDraft.startDate) {
      nextErrors.endDate = "End date is before the start date.";
    }
    const commitment = parseNumber(yearDraft.commitment);
    if (yearDraft.commitment.trim() && (Number.isNaN(commitment) || commitment < 0)) {
      nextErrors.commitment = "Enter an amount.";
    }
    const discount = parseNumber(yearDraft.discount);
    if (yearDraft.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) {
      nextErrors.discount = "Enter 0 to 100.";
    }
    setYearDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const normalized = {
      ...yearDraft,
      label: yearDraft.label.trim(),
      commitment: yearDraft.commitment.trim() ? formatCommitment(yearDraft.commitment) : "",
      discount: yearDraft.discount.trim(),
    };

    setForm((prev) => {
      if (editingYearId) {
        return {
          ...prev,
          years: prev.years.map((row) => (row.id === editingYearId ? { ...normalized, id: editingYearId } : row)),
        };
      }
      return { ...prev, years: [...prev.years, normalized] };
    });
    setErrors((prev) => {
      if (!prev.years?.[editingYearId || normalized.id]) return prev;
      const next = { ...prev, years: { ...prev.years } };
      delete next.years[editingYearId || normalized.id];
      if (!Object.keys(next.years).length) delete next.years;
      return next;
    });
    closeYearDialog();
  };

  const openTierDialog = () => {
    setEditingTierId(null);
    setTierDraft(emptyTier());
    setTierDraftErrors({});
    setTierDialogOpen(true);
  };

  const openEditTierDialog = (tier) => {
    setEditingTierId(tier.id);
    setTierDraft({ ...tier });
    setTierDraftErrors({});
    setTierDialogOpen(true);
  };

  const closeTierDialog = () => {
    setTierDialogOpen(false);
    setEditingTierId(null);
    setTierDraft(emptyTier());
    setTierDraftErrors({});
  };

  const setTierDraftField = (key, value) => {
    setTierDraft((prev) => ({ ...prev, [key]: value }));
    setTierDraftErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const saveTierDraft = () => {
    const nextErrors = {};
    if (!tierDraft.name.trim()) nextErrors.name = "Enter a tier name.";
    const threshold = parseNumber(tierDraft.threshold);
    if (tierDraft.threshold.trim() && (Number.isNaN(threshold) || threshold < 0)) {
      nextErrors.threshold = "Enter an amount.";
    }
    const discount = parseNumber(tierDraft.discount);
    if (tierDraft.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) {
      nextErrors.discount = "Enter 0 to 100.";
    }
    setTierDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const normalized = {
      ...tierDraft,
      name: tierDraft.name.trim(),
      threshold: tierDraft.threshold.trim() ? formatCommitment(tierDraft.threshold) : "",
      discount: tierDraft.discount.trim(),
    };

    setForm((prev) => {
      if (editingTierId) {
        return {
          ...prev,
          tiers: prev.tiers.map((row) => (row.id === editingTierId ? { ...normalized, id: editingTierId } : row)),
        };
      }
      return { ...prev, tiers: [...prev.tiers, normalized] };
    });
    closeTierDialog();
  };

  const validate = (draft) => {
    const next = {};
    if (!form.organizationName.trim()) next.organizationName = "Enter an organization name.";
    // Java onboarding always needs identity + at least one account (even for commercial DRAFT).
    if (!isEdit) {
      if (!form.primaryContactName.trim()) next.primaryContactName = "Enter a primary contact name.";
      if (!form.primaryContactEmail.trim()) {
        next.primaryContactEmail = "Enter a primary contact email.";
      }
      if (!selectedCustomerAccountIds(form).length) {
        next.payerAccountNumbers = "Select at least one AWS account for this customer.";
      }
    }
    if (!draft && !form.customerId.trim()) next.customerId = "Enter a customer ID.";
    if (form.primaryContactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.primaryContactEmail.trim())) {
      next.primaryContactEmail = "Enter a valid email address.";
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      next.endDate = "End date must be on or after the start date.";
    }
    if (form.multiYearCommitment.trim()) {
      const amount = parseNumber(form.multiYearCommitment);
      if (Number.isNaN(amount) || amount < 0) next.multiYearCommitment = "Enter a commitment amount.";
    }
    if (String(form.commitmentDiscount || "").trim()) {
      const discount = parseNumber(form.commitmentDiscount);
      if (Number.isNaN(discount) || discount < 0 || discount > 100) {
        next.commitmentDiscount = "Enter 0 to 100.";
      }
    }

    const commitmentMode = resolveCommitmentMode(form);
    if (commitmentMode === "annual") {
      const yearErrors = {};
      form.years.forEach((year) => {
        const blank = !year.label.trim() && !year.startDate && !year.endDate && !year.commitment.trim() && !year.discount.trim();
        if (blank) return;
        const row = {};
        if (!year.label.trim()) row.label = "Enter a program year identifier.";
        if (year.startDate && year.endDate && year.endDate < year.startDate) row.endDate = "End date is before the start date.";
        const commitment = parseNumber(year.commitment);
        if (year.commitment.trim() && (Number.isNaN(commitment) || commitment < 0)) row.commitment = "Enter an amount.";
        const discount = parseNumber(year.discount);
        if (year.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) row.discount = "Enter 0 to 100.";
        if (Object.keys(row).length) yearErrors[year.id] = row;
      });
      if (Object.keys(yearErrors).length) next.years = yearErrors;
    } else {
      const tierErrors = {};
      form.tiers.forEach((tier) => {
        const blank = !tier.name.trim() && !tier.threshold.trim() && !tier.discount.trim();
        if (blank) return;
        const row = {};
        if (!tier.name.trim()) row.name = "Enter a tier name.";
        const threshold = parseNumber(tier.threshold);
        if (tier.threshold.trim() && (Number.isNaN(threshold) || threshold < 0)) row.threshold = "Enter an amount.";
        const discount = parseNumber(tier.discount);
        if (tier.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) row.discount = "Enter 0 to 100.";
        if (Object.keys(row).length) tierErrors[tier.id] = row;
      });
      if (Object.keys(tierErrors).length) next.tiers = tierErrors;
    }

    return next;
  };

  const submit = async (status) => {
    if (saving) return;
    const draft = status === "draft";
    let nextErrors = validate(draft);
    if (!draft) {
      nextErrors = { ...nextErrors, ...validateActiveCustomer(form) };
    }
    setErrors(nextErrors);
    setFormError("");
    if (Object.keys(nextErrors).length) {
      document.querySelector("[aria-invalid='true']")?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        if (!form.customerOrgcode) {
          setSaving(false);
          setFormError("Customer organization code is missing; unable to update.");
          return;
        }
        // Persist only Selected Accounts (Payee dropdown), never scoped Payer-dropdown IDs.
        const result = await updateOrganizationCustomer(
          buildJavaCustomerUpdatePayload(form, { active: true, status })
        );
        const name = form.organizationName.trim() || form.customerId.trim() || "Customer";
        history.push(CUSTOMER_PATH, {
          notice:
            result?.message ||
            (draft ? `Customer draft saved: ${name}.` : `Customer updated: ${name}.`),
        });
      } else {
        // New customer: Java onboarding creates org + commercial profile together.
        await createOnboardedCustomer(buildJavaOnboardingPayload(form, status));
        const profile = await findPartnerCustomerProfile({
          organizationName: form.organizationName,
          primaryContactEmail: form.primaryContactEmail,
        });
        const name =
          profile?.organization_name ||
          form.organizationName.trim() ||
          profile?.customer_code ||
          "Customer";
        history.push(CUSTOMER_PATH, {
          notice: draft ? `Customer draft saved: ${name}.` : `Customer created: ${name}.`,
        });
      }
    } catch (error) {
      setSaving(false);
      setFormError(error?.message || "Unable to save the customer.");
    }
  };

  const helperProps = (key) => ({ sx: { mx: 0, color: errors[key] ? undefined : AH_MUTED } });

  const renderChipAccordion = ({ key, label, hint, options, emptySummary, getValue, getChipLabel }) => {
    const selected = form[key] || [];
    const selectedSet = new Set(selected);
    return (
      <Box sx={{ minWidth: 0 }}>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: `1px solid ${AH_BORDER}`,
            borderRadius: "8px !important",
            backgroundColor: AH_WHITE,
            "&:before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: AH_MUTED }} />}
            aria-controls={`${key}-panel`}
            id={`${key}-header`}
            sx={{
              minHeight: 48,
              px: 1.5,
              "& .MuiAccordionSummary-content": {
                my: 1,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                pr: 1,
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>{label}</Typography>
              {hint ? <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>{hint}</Typography> : null}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_MUTED, flexShrink: 0 }}>
              {chipSummary(selected.map(getChipLabel), emptySummary)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`${key}-panel`}
            sx={{ px: 1.5, pt: 0, pb: 1.5, borderTop: `1px solid ${AH_BORDER}`, backgroundColor: AH_WHITE }}
          >
            <Box role="group" aria-label={label} sx={{ display: "flex", flexWrap: "wrap", gap: 1, pt: 1.5 }}>
              {options.map((option) => {
                const value = getValue(option);
                const selectedOption = selectedSet.has(value);
                return (
                  <Chip
                    key={value}
                    label={getChipLabel(option)}
                    clickable
                    title={typeof option === "string" ? option : option.label}
                    aria-pressed={selectedOption}
                    onClick={() => toggleChip(key, value)}
                    sx={regionChipSx(selectedOption)}
                  />
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  if (!allowed) return null;

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid className="px-0">
          <Box sx={{ pt: 1.5, pb: 4, width: "100%", ...auditHubPageSx }}>
            <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED, mb: 1.5 }} aria-label="Breadcrumb">
              <Link component="button" type="button" underline="hover" color="inherit" onClick={leave} sx={{ fontSize: 13 }}>
                Customer
              </Link>
              <Typography sx={{ fontSize: 13, color: AH_TEXT }}>
                {isEdit ? "Edit Customer" : "Add Customer"}
              </Typography>
            </Breadcrumbs>

            <Box sx={tableShellSx}>
              <Typography
                component="h1"
                sx={{ m: 0, fontWeight: 700, fontSize: { xs: "1.125rem", md: "1.25rem" }, color: AH_TEXT, letterSpacing: "-0.01em", lineHeight: 1.3 }}
              >
                {isEdit ? "Edit Customer" : "Add Customer"}
              </Typography>
              <Typography sx={{ mt: 0.25, mb: 2.5, fontSize: 13, color: AH_MUTED }}>
                {isEdit
                  ? "Update downstream customer commercial terms and account mappings"
                  : "Onboard a new downstream customer with commercial terms and discount structure"}
              </Typography>

              {loadingRecord ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={32} sx={{ color: AH_ACCENT }} />
                </Box>
              ) : null}

              {!loadingRecord ? (
              <>
              {/* 1. Organization Details */}
              <Typography component="h3" sx={{ ...sectionSx, mt: 0, mb: 1.5 }}>Organization Details</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="organizationName">Organization Name</FieldLabel>
                  <TextField
                    id="organizationName"
                    size="small"
                    fullWidth
                    value={form.organizationName}
                    onChange={(event) => setField("organizationName", event.target.value)}
                    placeholder="e.g., TechGlobal Corp"
                    error={Boolean(errors.organizationName)}
                    helperText={errors.organizationName || ""}
                    FormHelperTextProps={helperProps("organizationName")}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="customerId">Customer ID</FieldLabel>
                  <TextField
                    id="customerId"
                    size="small"
                    fullWidth
                    value={form.customerId}
                    onChange={(event) => setField("customerId", event.target.value)}
                    placeholder="e.g., CUST-10042"
                    error={Boolean(errors.customerId)}
                    helperText={errors.customerId || "Unique internal identifier for ERP, billing, and CRM linkage"}
                    FormHelperTextProps={helperProps("customerId")}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="primaryContactName">Primary Contact Name</FieldLabel>
                  <TextField
                    id="primaryContactName"
                    size="small"
                    fullWidth
                    value={form.primaryContactName}
                    onChange={(event) => setField("primaryContactName", event.target.value)}
                    placeholder="e.g., Jane Smith"
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="primaryContactEmail">Primary Contact Email</FieldLabel>
                  <TextField
                    id="primaryContactEmail"
                    size="small"
                    fullWidth
                    value={form.primaryContactEmail}
                    onChange={(event) => setField("primaryContactEmail", event.target.value)}
                    placeholder="e.g., jane.smith@acmecorp.com"
                    error={Boolean(errors.primaryContactEmail)}
                    helperText={errors.primaryContactEmail || ""}
                    FormHelperTextProps={helperProps("primaryContactEmail")}
                    sx={fieldSx}
                  />
                </Box>
              </Box>

              {/* 2. AWS Account & Regions */}
              <Typography component="h3" sx={{ ...sectionSx, mt: 2.5, mb: 1.5 }}>AWS Account & Regions</Typography>
              <Box sx={{ display: "grid", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <PayerPayeeAccountsField
                    key={
                      isEdit
                        ? `edit-${customerId}-${payersHydrated ? "ready" : "loading"}`
                        : "create"
                    }
                    id="payerAccountNumbers"
                    label="AWS Payer / Payee Accounts"
                    placeholder="Select accounts..."
                    hint="Link the AWS payer accounts associated with this customer"
                    payerOptions={payerAccounts}
                    linkedAccounts={payerAccounts}
                    payerAccountNumbers={form.payerAccountNumbers}
                    payeeAccountNumbers={form.payeeAccountNumbers}
                    payeeUiPayerNumbers={form.payeeUiPayerNumbers}
                    onAccountsMapChange={(_map, meta = {}) => {
                      setForm((prev) => {
                        const next = { ...prev };
                        if (Array.isArray(meta.payerAccountNumbers)) {
                          next.payerAccountNumbers = meta.payerAccountNumbers;
                        }
                        if (Array.isArray(meta.payeeAccountNumbers)) {
                          next.payeeAccountNumbers = meta.payeeAccountNumbers;
                        }
                        if (Array.isArray(meta.payeeUiPayerNumbers)) {
                          next.payeeUiPayerNumbers = meta.payeeUiPayerNumbers;
                        }
                        return next;
                      });
                      setErrors((prev) => {
                        if (!prev.payerAccountNumbers) return prev;
                        const next = { ...prev };
                        delete next.payerAccountNumbers;
                        return next;
                      });
                    }}
                    fieldSx={fieldSx}
                  />
                  {errors.payerAccountNumbers ? (
                    <Typography sx={{ mt: 0.5, fontSize: 12, color: "#d32f2f" }}>
                      {errors.payerAccountNumbers}
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      border: `1px solid ${AH_BORDER}`,
                      borderRadius: "8px !important",
                      backgroundColor: AH_WHITE,
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: AH_MUTED }} />}
                      aria-controls="regions-panel"
                      id="regions-header"
                      sx={{
                        minHeight: 48,
                        px: 1.5,
                        "& .MuiAccordionSummary-content": {
                          my: 1,
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          pr: 1,
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>
                          Primary Operating Regions
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                          Select AWS regions where this customer primarily operates
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_MUTED, flexShrink: 0 }}>
                        {regionsSummary(form.regions)}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      id="regions-panel"
                      sx={{ px: 1.5, pt: 0, pb: 1.5, borderTop: `1px solid ${AH_BORDER}`, backgroundColor: AH_WHITE }}
                    >
                      <Box role="group" aria-label="Primary Operating Regions" sx={{ display: "flex", flexWrap: "wrap", gap: 1, pt: 1.5 }}>
                        <Chip
                          label="All Regions"
                          clickable
                          aria-pressed={form.regions.length === 0}
                          onClick={() => setField("regions", [])}
                          sx={regionChipSx(form.regions.length === 0)}
                        />
                        {AWS_REGION_CODES.map((code) => {
                          const selected = form.regions.includes(code);
                          return (
                            <Chip
                              key={code}
                              label={code}
                              clickable
                              aria-pressed={selected}
                              onClick={() => toggleRegion(code)}
                              sx={regionChipSx(selected)}
                            />
                          );
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Box>

              {/* 3. Commercial Contract Details */}
              <Typography component="h3" sx={{ ...sectionSx, mt: 2.5, mb: 1.5 }}>Commercial Contract Details</Typography>
              <Box sx={{ display: "grid", gap: 2 }}>
                <WholesaleProgramsField
                  id="wholesalePrograms"
                  options={availablePrograms}
                  value={form.wholesalePrograms || []}
                  onChange={(next) => setField("wholesalePrograms", next)}
                  loading={programsLoading}
                  error={programsError}
                  fieldError={errors.wholesalePrograms || ""}
                  helperText="Programs governing the upstream discount applied to this customer"
                  fieldSx={fieldSx}
                />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                  <Box sx={{ minWidth: 0, width: "100%" }}>
                    <FieldLabel htmlFor="startDate" required>
                      Contract Start Date
                    </FieldLabel>
                    <DueDateField
                      id="startDate"
                      value={form.startDate}
                      onChange={(value) => {
                        setField("startDate", value);
                        if (form.endDate && value && form.endDate <= value) setField("endDate", "");
                      }}
                      fullWidth
                      showLabel={false}
                      size="small"
                      minDate={null}
                      required
                      ariaLabel="Contract start date"
                      placeholder="Select the Start Date"
                      portalId="root"
                    />
                  </Box>
                  <Box sx={{ minWidth: 0, width: "100%" }}>
                    <FieldLabel htmlFor="endDate" required>
                      Contract End Date
                    </FieldLabel>
                    <DueDateField
                      id="endDate"
                      value={form.endDate}
                      onChange={(value) => setField("endDate", value)}
                      fullWidth
                      showLabel={false}
                      size="small"
                      minDate={dayAfterIso(form.startDate)}
                      required
                      ariaLabel="Contract end date"
                      placeholder="Select the End Date"
                      error={Boolean(errors.endDate)}
                      helperText={errors.endDate || ""}
                      portalId="root"
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel htmlFor="paymentTerms">Payment Terms</FieldLabel>
                    <TextField
                      id="paymentTerms"
                      select
                      size="small"
                      fullWidth
                      value={form.paymentTerms || ""}
                      onChange={(event) => setField("paymentTerms", event.target.value)}
                      sx={selectFieldSx(Boolean(form.paymentTerms))}
                      SelectProps={buildSelectProps({ placeholder: "Select terms..." })}
                    >
                      {PAYMENT_TERMS.map((term) => (
                        <MenuItem key={term} value={term} sx={menuItemSx}>{term}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Box>

              {/* 4. Commitment Details */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 2.5, mb: 1.5 }}>
                <Typography component="h3" sx={{ ...sectionSx, m: 0 }}>Commitment Details</Typography>
              </Box>
              <Box sx={{ display: "grid", gap: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel htmlFor="multiYearCommitment">Multi-Year Commitment Amount ($)</FieldLabel>
                    <TextField
                      id="multiYearCommitment"
                      size="small"
                      fullWidth
                      value={form.multiYearCommitment}
                      onChange={(event) => setField("multiYearCommitment", event.target.value)}
                      onBlur={() => {
                        if (!form.multiYearCommitment.trim() || errors.multiYearCommitment) return;
                        const amount = parseNumber(form.multiYearCommitment);
                        if (amount != null && !Number.isNaN(amount)) {
                          setField("multiYearCommitment", formatCommitment(form.multiYearCommitment));
                        }
                      }}
                      placeholder="1,500,000"
                      error={Boolean(errors.multiYearCommitment)}
                      helperText={errors.multiYearCommitment || "Overall multi-year spend obligation across the full contract term"}
                      FormHelperTextProps={helperProps("multiYearCommitment")}
                      inputProps={{ inputMode: "decimal" }}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      sx={fieldSx}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <FieldLabel htmlFor="commitmentDiscount">Discount (%)</FieldLabel>
                    <TextField
                      id="commitmentDiscount"
                      size="small"
                      fullWidth
                      value={form.commitmentDiscount}
                      onChange={(event) => setField("commitmentDiscount", event.target.value)}
                      placeholder="0.00"
                      error={Boolean(errors.commitmentDiscount)}
                      helperText={errors.commitmentDiscount || ""}
                      FormHelperTextProps={helperProps("commitmentDiscount")}
                      inputProps={{ inputMode: "decimal" }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={fieldSx}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT, mb: 1 }}>
                    Commitment Structure
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: AH_MUTED, mb: 1.25, lineHeight: 1.4 }}>
                    Choose one structure for this customer. Only the selected option is saved.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1.25,
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={resolveCommitmentMode(form)}
                      onChange={(_event, next) => {
                        if (!next) return;
                        // Keep both drafts in memory while switching; only the active mode is saved.
                        setForm((prev) => ({
                          ...prev,
                          commitmentMode: next,
                        }));
                        setErrors((prev) => {
                          if (!prev.years && !prev.tiers) return prev;
                          const nextErrors = { ...prev };
                          delete nextErrors.years;
                          delete nextErrors.tiers;
                          return nextErrors;
                        });
                      }}
                      aria-label="Commitment structure"
                      sx={commitmentModeToggleSx}
                    >
                      <ToggleButton value="annual">Annual Commitment Breakdown</ToggleButton>
                      <ToggleButton value="tiers">Downstream Volume Discount Tiers</ToggleButton>
                    </ToggleButtonGroup>
                    {resolveCommitmentMode(form) === "annual" ? (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                        onClick={openYearDialog}
                        sx={{ ...accentOutlinedButtonSx, height: 36, minHeight: 36, px: 1.5, flexShrink: 0 }}
                      >
                        Add Year
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                        onClick={openTierDialog}
                        sx={{ ...accentOutlinedButtonSx, height: 36, minHeight: 36, px: 1.5, flexShrink: 0 }}
                      >
                        Add Tier
                      </Button>
                    )}
                  </Box>

                  {resolveCommitmentMode(form) === "annual" ? (
                      <TableContainer sx={formTableCardSx}>
                        <Table size="small" aria-label="Annual commitment breakdown">
                          <TableHead>
                            <TableRow>
                              {["YEAR", "START DATE", "END DATE", "COMMITMENT ($)", "DISCOUNT (%)", "ACTIONS"].map((heading) => (
                                <TableCell key={heading} sx={formTableHeadCellSx}>{heading}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {form.years.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ ...formTableBodyCellSx, py: 4, borderBottom: 0, color: AH_MUTED }}>
                                  No annual commitment rows yet.
                                </TableCell>
                              </TableRow>
                            ) : form.years.map((year) => {
                              const rowErrors = errors.years?.[year.id] || {};
                              return (
                                <TableRow key={year.id} hover>
                                  <TableCell sx={{ ...formTableBodyCellSx, minWidth: 120 }}>
                                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.label ? "#d32f2f" : AH_TEXT, fontWeight: 600 }}>
                                      {year.label || "—"}
                                    </Typography>
                                    {rowErrors.label ? (
                                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.label}</Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell sx={{ ...formTableBodyCellSx, minWidth: 110 }}>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#334155" }}>{formatDisplayDate(year.startDate)}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ ...formTableBodyCellSx, minWidth: 110 }}>
                                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.endDate ? "#d32f2f" : "#334155" }}>
                                      {formatDisplayDate(year.endDate)}
                                    </Typography>
                                    {rowErrors.endDate ? (
                                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.endDate}</Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell sx={{ ...formTableBodyCellSx, minWidth: 130 }}>
                                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.commitment ? "#d32f2f" : "#334155" }}>
                                      {year.commitment ? `$${year.commitment}` : "—"}
                                    </Typography>
                                    {rowErrors.commitment ? (
                                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.commitment}</Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell sx={{ ...formTableBodyCellSx, minWidth: 100 }}>
                                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.discount ? "#d32f2f" : "#334155" }}>
                                      {year.discount ? `${year.discount}%` : "—"}
                                    </Typography>
                                    {rowErrors.discount ? (
                                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.discount}</Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell sx={{ ...formTableBodyCellSx, width: 96, whiteSpace: "nowrap" }}>
                                    <Box sx={formTableActionsWrapSx}>
                                      <Tooltip title="Edit year">
                                        <IconButton aria-label={`Edit ${year.label || "year"}`} onClick={() => openEditYearDialog(year)} size="small" sx={formTableActionBtnSx}>
                                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Remove year">
                                        <IconButton
                                          aria-label={`Remove ${year.label || "year"}`}
                                          onClick={() => setForm((prev) => ({ ...prev, years: prev.years.filter((row) => row.id !== year.id) }))}
                                          size="small"
                                          sx={formTableDeleteBtnSx}
                                        >
                                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                  ) : (
                      <TableContainer sx={formTableCardSx}>
                        <Table size="small" aria-label="Downstream volume discount tiers">
                          <TableHead>
                            <TableRow>
                              {["TIER NAME", "MIN SPEND THRESHOLD ($)", "ACCELERATED RETAIL DISCOUNT (%)", "ACTIONS"].map((heading) => (
                                <TableCell key={heading} sx={formTableHeadCellSx}>{heading}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {form.tiers.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ ...formTableBodyCellSx, py: 4, borderBottom: 0, color: AH_MUTED }}>
                                  No volume discount tiers yet.
                        </TableCell>
                      </TableRow>
                    ) : form.tiers.map((tier) => {
                      const rowErrors = errors.tiers?.[tier.id] || {};
                      return (
                        <TableRow key={tier.id} hover>
                          <TableCell sx={{ ...formTableBodyCellSx, minWidth: 140 }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.name ? "#d32f2f" : AH_TEXT, fontWeight: 600 }}>
                              {tier.name || "—"}
                            </Typography>
                            {rowErrors.name ? (
                              <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.name}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ ...formTableBodyCellSx, minWidth: 140 }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.threshold ? "#d32f2f" : "#334155" }}>
                              {tier.threshold ? `$${tier.threshold}` : "—"}
                            </Typography>
                            {rowErrors.threshold ? (
                              <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.threshold}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ ...formTableBodyCellSx, minWidth: 160 }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.discount ? "#d32f2f" : "#334155" }}>
                              {tier.discount ? `${tier.discount}%` : "—"}
                            </Typography>
                            {rowErrors.discount ? (
                              <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.discount}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ ...formTableBodyCellSx, width: 96, whiteSpace: "nowrap" }}>
                            <Box sx={formTableActionsWrapSx}>
                              <Tooltip title="Edit tier">
                                <IconButton aria-label={`Edit ${tier.name || "tier"}`} onClick={() => openEditTierDialog(tier)} size="small" sx={formTableActionBtnSx}>
                                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove tier">
                                <IconButton
                                  aria-label={`Remove ${tier.name || "tier"}`}
                                  onClick={() => setForm((prev) => ({ ...prev, tiers: prev.tiers.filter((row) => row.id !== tier.id) }))}
                                  size="small"
                                  sx={formTableDeleteBtnSx}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                  )}
                </Box>
              </Box>

              {/* 6. Pricing Model */}
              <Typography component="h3" sx={{ ...sectionSx, mt: 2.5, mb: 1.5 }}>Pricing Model</Typography>
              <Box sx={{ display: "grid", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="pricingModel">Pricing Model</FieldLabel>
                  <TextField
                    id="pricingModel"
                    select
                    size="small"
                    fullWidth
                    value={form.pricingModel || ""}
                    onChange={(event) => setField("pricingModel", event.target.value)}
                    helperText="Defines how downstream retail pricing is calculated from wholesale rates"
                    FormHelperTextProps={helperProps("pricingModel")}
                    sx={selectFieldSx(Boolean(form.pricingModel))}
                    SelectProps={buildSelectProps({ placeholder: "Select pricing model..." })}
                  >
                    {PRICING_MODELS.map((model) => (
                      <MenuItem key={model} value={model} sx={menuItemSx}>{model}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                {renderChipAccordion({
                  key: "serviceExclusions",
                  label: "Service Exclusions",
                  hint: "Block discount application for selected AWS service categories",
                  options: SERVICE_EXCLUSIONS,
                  emptySummary: "None selected",
                  getValue: (option) => option,
                  getChipLabel: (option) => option,
                })}
              </Box>

              {formError ? (
                <Typography sx={{ mt: 2.5, fontSize: 13, color: "#d32f2f", lineHeight: 1.5 }} role="alert">
                  {formError}
                </Typography>
              ) : null}

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mt: 3,
                  pt: 2,
                  borderTop: `1px solid ${AH_BORDER}`,
                }}
              >
                <Button
                  variant="contained"
                  onClick={leave}
                  disabled={saving}
                  sx={{
                    ...outlinedButtonSx,
                    backgroundColor: "#74788d",
                    color: AH_WHITE,
                    borderColor: "#74788d",
                    "&:hover": { backgroundColor: "#686c7f", borderColor: "#686c7f", boxShadow: "none" },
                  }}
                >
                  Cancel
                </Button>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: "auto" }}>
                  <Button
                    variant="outlined"
                    startIcon={<SaveOutlinedIcon />}
                    onClick={() => submit("draft")}
                    disabled={saving}
                    sx={{
                      ...outlinedButtonSx,
                      backgroundColor: AH_WHITE,
                      color: AH_ACCENT,
                      borderColor: AH_ACCENT,
                      "&:hover": {
                        backgroundColor: AH_WHITE,
                        color: AH_ACCENT_HOVER,
                        borderColor: AH_ACCENT_HOVER,
                        boxShadow: "none",
                      },
                    }}
                  >
                    {saving ? "Saving…" : "Save Draft"}
                  </Button>
                  <Button variant="contained" onClick={() => submit("saved")} disabled={saving} sx={primaryButtonSx}>
                    {saving ? "Saving…" : isEdit ? "Update Customer" : "Create Customer"}
                  </Button>
                </Box>
              </Box>
              </>
              ) : null}
            </Box>
          </Box>
        </Container>

        {/* Add / Edit Year dialog */}
        <Dialog
          open={yearDialogOpen}
          onClose={closeYearDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 560 } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
              px: 3,
              backgroundColor: AH_ACCENT,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
              {editingYearId ? "Edit Program Year" : "Add Program Year"}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label="Close"
              onClick={closeYearDialog}
              sx={{
                p: 0,
                m: 0,
                border: "none",
                background: "transparent",
                color: AH_WHITE,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </Box>
          </Box>
          <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="year-draft-label">Program Year Identifier</FieldLabel>
                <TextField
                  id="year-draft-label"
                  size="small"
                  fullWidth
                  value={yearDraft.label}
                  onChange={(event) => setYearDraftField("label", event.target.value)}
                  placeholder="e.g., Year 1"
                  error={Boolean(yearDraftErrors.label)}
                  helperText={yearDraftErrors.label || ""}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <FieldLabel htmlFor="year-draft-start">Annual Commitment Start Date</FieldLabel>
                  <DueDateField
                    id="year-draft-start"
                    value={yearDraft.startDate}
                    onChange={(value) => setYearDraftField("startDate", value)}
                    fullWidth
                    showLabel={false}
                    size="small"
                    minDate={null}
                    ariaLabel="Annual commitment start date"
                    placeholder="Select the Start Date"
                    portalId="root"
                  />
                </Box>
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <FieldLabel htmlFor="year-draft-end">Annual Commitment End Date</FieldLabel>
                  <DueDateField
                    id="year-draft-end"
                    value={yearDraft.endDate}
                    onChange={(value) => setYearDraftField("endDate", value)}
                    fullWidth
                    showLabel={false}
                    size="small"
                    minDate={dayAfterIso(yearDraft.startDate)}
                    ariaLabel="Annual commitment end date"
                    placeholder="Select the End Date"
                    error={Boolean(yearDraftErrors.endDate)}
                    helperText={yearDraftErrors.endDate || ""}
                    portalId="root"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="year-draft-commitment">Annual Spend Commitment Target ($)</FieldLabel>
                  <TextField
                    id="year-draft-commitment"
                    size="small"
                    fullWidth
                    value={yearDraft.commitment}
                    onChange={(event) => setYearDraftField("commitment", event.target.value)}
                    onBlur={() => {
                      if (!yearDraft.commitment.trim() || yearDraftErrors.commitment) return;
                      const amount = parseNumber(yearDraft.commitment);
                      if (amount != null && !Number.isNaN(amount)) {
                        setYearDraftField("commitment", formatCommitment(yearDraft.commitment));
                      }
                    }}
                    placeholder="0"
                    error={Boolean(yearDraftErrors.commitment)}
                    helperText={yearDraftErrors.commitment || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="year-draft-discount">Guaranteed Downstream Discount (%)</FieldLabel>
                  <TextField
                    id="year-draft-discount"
                    size="small"
                    fullWidth
                    value={yearDraft.discount}
                    onChange={(event) => setYearDraftField("discount", event.target.value)}
                    placeholder="0.00"
                    error={Boolean(yearDraftErrors.discount)}
                    helperText={yearDraftErrors.discount || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 3,
              py: 2,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: "#f8fafc",
            }}
          >
            <Button
              variant="contained"
              onClick={closeYearDialog}
              sx={{ ...primaryButtonSx, height: 36, minHeight: 36, backgroundColor: "#74788d", "&:hover": { backgroundColor: "#5f6478", boxShadow: "none" } }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={saveYearDraft} sx={{ ...primaryButtonSx, height: 36, minHeight: 36 }}>
              {editingYearId ? "Save Year" : "Add Year"}
            </Button>
          </Box>
        </Dialog>

        {/* Add / Edit Tier dialog */}
        <Dialog
          open={tierDialogOpen}
          onClose={closeTierDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 560 } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
              px: 3,
              backgroundColor: AH_ACCENT,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
              {editingTierId ? "Edit Volume Tier" : "Add Volume Tier"}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label="Close"
              onClick={closeTierDialog}
              sx={{
                p: 0,
                m: 0,
                border: "none",
                background: "transparent",
                color: AH_WHITE,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </Box>
          </Box>
          <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="tier-draft-name">Tier Name</FieldLabel>
                <TextField
                  id="tier-draft-name"
                  size="small"
                  fullWidth
                  value={tierDraft.name}
                  onChange={(event) => setTierDraftField("name", event.target.value)}
                  placeholder="e.g., Tier 1 Growth"
                  error={Boolean(tierDraftErrors.name)}
                  helperText={tierDraftErrors.name || ""}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="tier-draft-threshold">Min Spend Threshold ($)</FieldLabel>
                  <TextField
                    id="tier-draft-threshold"
                    size="small"
                    fullWidth
                    value={tierDraft.threshold}
                    onChange={(event) => setTierDraftField("threshold", event.target.value)}
                    onBlur={() => {
                      if (!tierDraft.threshold.trim() || tierDraftErrors.threshold) return;
                      const amount = parseNumber(tierDraft.threshold);
                      if (amount != null && !Number.isNaN(amount)) {
                        setTierDraftField("threshold", formatCommitment(tierDraft.threshold));
                      }
                    }}
                    placeholder="0"
                    error={Boolean(tierDraftErrors.threshold)}
                    helperText={tierDraftErrors.threshold || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="tier-draft-discount">Accelerated Retail Discount (%)</FieldLabel>
                  <TextField
                    id="tier-draft-discount"
                    size="small"
                    fullWidth
                    value={tierDraft.discount}
                    onChange={(event) => setTierDraftField("discount", event.target.value)}
                    placeholder="0.00"
                    error={Boolean(tierDraftErrors.discount)}
                    helperText={tierDraftErrors.discount || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 3,
              py: 2,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: "#f8fafc",
            }}
          >
            <Button
              variant="contained"
              onClick={closeTierDialog}
              sx={{ ...primaryButtonSx, height: 36, minHeight: 36, backgroundColor: "#74788d", "&:hover": { backgroundColor: "#5f6478", boxShadow: "none" } }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={saveTierDraft} sx={{ ...primaryButtonSx, height: 36, minHeight: 36 }}>
              {editingTierId ? "Save Tier" : "Add Tier"}
            </Button>
          </Box>
        </Dialog>
      </div>
    </AuditHubThemeProvider>
  );
};

export default AddCustomer;
